import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { ReactComponent as LinkedInIcon } from '../../../../../assets/images/linkedin-import-icon.svg';
import { ReactComponent as WarningIcon } from '../../../../../assets/images/RedWarningIcon.svg';
import { ReactComponent as TimeLapseIcon } from '../../../../../assets/images/timelapse.svg';
import Button from '../../../../../components/shared/Button/Button';
import { COUNTER_START_VALUE, LINKEDIN_CONNECT } from '../../../../../constants/commonConstants';
import { unsetLinkedInImportTime } from '../../../../../redux/actions/linkedInImportActions';
import urls from '../../../../../routing/urls';

import InstructionsModal from './InstructionsModal/InstructionsModal';

import styles from './LinkedInImport.module.scss';

const LinkedInImport = ({ showCounter }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let counterRef = null;
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(null);
  const importStartTime = useSelector((state) => state.linkedInImportReducer.importStartTime);
  const showLinkedInImportTimer = useSelector((state) => state.linkedInImportReducer.showLinkedInImportTimer);

  const duration = useCallback(
    (currentTime) => COUNTER_START_VALUE - moment.duration(moment(currentTime).diff(importStartTime)).asMinutes(),
    [importStartTime]
  );

  useEffect(() => {
    if (importStartTime) {
      setElapsedTime(duration(moment.now()));
      counterRef = setInterval(() => {
        const remainingTime = duration(moment.now());
        setElapsedTime(remainingTime);

        // Clear interval when time reaches 0 or goes negative
        if (remainingTime <= 0) {
          clearInterval(counterRef);
          dispatch(unsetLinkedInImportTime());
        }
      }, 60000); // Check every minute
    }
    return () => {
      clearInterval(counterRef);
    };
  }, [importStartTime, duration, dispatch]);

  return (
    <>
      <div className={styles.linkedinImportContainer}>
        <div className={styles.innerContainer}>
          <div className={styles.iconContainer}>
            <LinkedInIcon />
            <div className={styles.boldText}>
              <div>{t('network.linkedin')}</div>
              <div className={styles.lightText}>{t('linkedinNetwork.addConnections')}</div>
            </div>
          </div>
          {!showLinkedInImportTimer && (
            <Button className={styles.linkedinBtn} variant="primary" onClick={() => setShowInstructionsModal(true)}>
              {t('linkedinNetwork.importLinkedin')}
            </Button>
          )}
          {showLinkedInImportTimer && (
            <button type="button" className={styles.importFileBtn} onClick={() => navigate(urls.linkedinZipFileImport)}>
              {t('linkedinNetwork.importFile')}
            </button>
          )}
        </div>
        <div className={styles.innerContainer}>
          <div>
            <div className={styles.text}>{t('linkedinNetwork.exportsInclude')}</div>
            <div className={styles.tagsContainer}>
              <div className={styles.tag}>{t('linkedinNetwork.name')}</div>
              <div className={styles.tag}>{t('linkedinNetwork.currentJob')}</div>
              <div className={styles.tag}>{t('linkedinNetwork.currentCompany')}</div>
            </div>
          </div>
        </div>
        {showLinkedInImportTimer && (
          <div className={styles.innerContainer}>
            <div className={styles.timeContainer}>
              {importStartTime && elapsedTime > 0 ? (
                <>
                  <TimeLapseIcon />
                  <div className={styles.timeText}>
                    <div>{t('linkedinNetwork.estimatedTimeRemaining')}</div>
                    <div className={styles.text}>
                      {elapsedTime >= 60
                        ? `~${Math.round(elapsedTime / 60)} ${t('linkedinNetwork.hours')}`
                        : `~${elapsedTime} ${t('linkedinNetwork.minutes')}`}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <WarningIcon />
                  <div className={styles.timeText}>
                    <div>{t('linkedinNetwork.exportReady')}</div>
                  </div>
                </>
              )}
            </div>

            <button type="button" className={styles.linkedInBtn} onClick={() => window.open(LINKEDIN_CONNECT)}>
              {t('linkedinNetwork.checkLinkedin')}
            </button>
          </div>
        )}
        {showCounter && (
          <div className={styles.pastImportCont}>
            <button type="button" className={styles.viewPastLink} onClick={() => navigate(urls.linkedinImportHistory)}>
              {t('syncSources.viewPastImports')}
            </button>
          </div>
        )}
      </div>
      <InstructionsModal isOpen={showInstructionsModal} onHide={() => setShowInstructionsModal(false)} />
    </>
  );
};

export default LinkedInImport;
